import { IRestClient, RestClient } from 'services/clients/RestClient';
import {
  CheckStoreResponse,
  DistrictAPI,
  EmergencyRelationsAPI,
  LicenseTypeAPI,
  LmTruckTypesAPI,
  PlateColorAPI,
  PostalCodeAPI,
  ProvinceApiResponse,
  SubDistrictAPI,
  UpdateInfoResponse,
  VehicleTypeAPI,
} from './Address.interface';
import { IStoreResponse } from './Store.interface';

const baseURL = process.env.REACT_APP_ADDRESS_AND_ZONING_BASE_URL || '';
const baseURLVMP = process.env.REACT_APP_VMP_URL || '';
const apiKey = process.env.REACT_APP_DRIVER_PORTAL_API_KEY || '';
const apiKeyALLNOW = process.env.REACT_APP_DRIVER_PORTAL_ALL_NOW_KEY || '';

export class AddressService {
  private restClient: IRestClient;

  constructor() {
    this.restClient = new RestClient({
      baseURL: baseURLVMP,
    });
  }

  private buildHeaders() {
    return { 'x-api-key': apiKey };
  }

  private buildHeadersNew() {
    const h: Record<string, string> = { Accept: 'application/json' };
    return h;
  }

  private buildHeadersALLNOW() {
    return { 'x-api-key': apiKeyALLNOW };
  }

  async provinces(): Promise<ProvinceApiResponse> {
    // const path = 'https://dev-vmp.allnowgroup.com/open-api/rider/getProvinces';
    const path = '/open-api/rider/getProvinces';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as ProvinceApiResponse;
  }

  async districts(request: any): Promise<DistrictAPI> {
    const provinceId = request?.province_id || request?.provinceId || '';

    const path = `/open-api/rider/getDistricts?province_id=${provinceId}`;
    const headers = this.buildHeadersNew();

    const response = await this.restClient.get(path, {
      headers,
    });

    return response as DistrictAPI;
  }

  async subDistricts(request: any): Promise<SubDistrictAPI> {
    const districtNames = Array.isArray(request?.districtNames)
      ? request.districtNames
          .map((name: unknown) => String(name || '').trim())
          .filter(Boolean)
      : [];
    const params = new URLSearchParams();
    if (districtNames.length > 0) {
      params.set('name', districtNames.join(','));
    } else {
      params.set('district_id', request?.districtId ?? '');
    }
    const path = `/open-api/rider/getSubDistricts?${params.toString()}`;
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as SubDistrictAPI;
  }

  async postalCodes(request: any): Promise<PostalCodeAPI> {
    const path =
      `/open-api/rider/getPostalCodes?sub_district_id=${request?.subDistrictId}` ??
      '';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as PostalCodeAPI;
  }

  async LmRiderTruckTypes(): Promise<LmTruckTypesAPI> {
    const path = 'open-api/drop-down/master-rider-type';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as LmTruckTypesAPI;
  }

  async LmTruckTypes(): Promise<LmTruckTypesAPI> {
    const path = 'open-api/rider/getLmTruckTypes';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as LmTruckTypesAPI;
  }

  async Interested(): Promise<LmTruckTypesAPI> {
    const path = 'open-api/drop-down/master-interested-company';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as LmTruckTypesAPI;
  }

  async plateColor(): Promise<PlateColorAPI> {
    const path = 'open-api/rider/license-plate-color';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as PlateColorAPI;
  }

  async EmergencyRelations(): Promise<EmergencyRelationsAPI> {
    const path = 'open-api/rider/getEmergencyRelations';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as EmergencyRelationsAPI;
  }

  async getCheckStore(request: any): Promise<CheckStoreResponse> {
    const path = `/api/rider/findBuCustomerByBuSiteId/${request ?? ''}`;
    const headers = this.buildHeadersNew();
    const response: CheckStoreResponse = await this.restClient.get(path, {
      headers,
    });
    return response;
  }

  async getStore(request: any): Promise<IStoreResponse[]> {
    const path = `/open-api/rider/getStore?prefix=${
      request?.sub_district_id ?? ''
    }`;
    const headers = this.buildHeadersNew();
    const response: IStoreResponse[] = await this.restClient.get(path, {
      headers,
    });
    return response;
  }

  async getStoreNew(
    request?: { prefix?: string; buCustomerId?: string } | string,
  ): Promise<IStoreResponse[]> {
    const prefix =
      typeof request === 'string'
        ? request.trim()
        : String(request?.prefix ?? '').trim();

    const params = new URLSearchParams();
    const buCustomerId =
      typeof request !== 'string' ? request?.buCustomerId : undefined;

    if (buCustomerId === '3,2') {
      params.set('buCustomerId', buCustomerId);
      if (prefix) params.set('prefix', prefix);
    } else {
      if (prefix) params.set('prefix', prefix);
      if (buCustomerId) params.set('buCustomerId', buCustomerId);
    }

    const path = `/open-api/rider/getStore?${params.toString()}`;

    const headers = this.buildHeadersNew();
    const response: IStoreResponse[] = await this.restClient.get(path, {
      headers,
    });
    return response;
  }

  async getLicenseTypeMaster(): Promise<LicenseTypeAPI> {
    const path = '/open-api/rider/license-type';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as LicenseTypeAPI;
  }

  async getVehicleOwnerType(): Promise<VehicleTypeAPI> {
    const path = 'open-api/rider/vehicle-owner-type';
    const headers = this.buildHeadersNew();
    const response = await this.restClient.get(path, {
      headers,
    });
    return response as VehicleTypeAPI;
  }

  async updateInfo(request: any): Promise<UpdateInfoResponse> {
    const path = '/api/rider/update-info';
    const headers = this.buildHeadersNew();
    const data =
      request instanceof FormData
        ? request
        : {
            ...request,
            type: 'save',
          };

    if (data instanceof FormData) {
      data.set('type', 'save');
    }

    return this.restClient.post<UpdateInfoResponse>(path, {
      data,
      headers,
    });
  }

  async updateVoluntaryInsurance(request: FormData): Promise<any> {
    const path = '/api/rider/rider-vmp-update-potential-lead';
    const headers = this.buildHeadersNew();
    return this.restClient.post<any>(path, {
      data: request,
      headers,
    });
  }
}
